import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Table } from "antd";
import { MONEY_TITLE_RE, PARTY_TITLE_RE, isFigure, moneyToneClass } from "./dataColors";

// Colour by meaning (2026-10-09, customer mock-ups): money columns green (red when
// negative, grey at zero), the customer/supplier column bold blue. Decided from the
// column title, so every existing page picks it up without being touched. A money
// column only colours cells that are actually figures unless it is right-aligned,
// so a "Due Date" column stays plain. `colour: false` on a column opts it out.
const tintColumn = (col) => {
  if (col.children) return { ...col, children: col.children.map(tintColumn) };
  if (col.colour === false || typeof col.title !== "string") return col;
  const isMoney = MONEY_TITLE_RE.test(col.title);
  const isParty = !isMoney && PARTY_TITLE_RE.test(col.title);
  if (!isMoney && !isParty) return col;
  const baseRender = col.render || ((v) => v);
  return {
    ...col,
    render: (value, record, index) => {
      const out = baseRender(value, record, index);
      const primitive = typeof out === "string" || typeof out === "number";
      if (!primitive && !React.isValidElement(out)) return out;
      if (isParty) return <span className="tx-party">{out}</span>;
      if (col.align !== "right" && !(primitive && isFigure(out))) return out;
      return <span className={`tx-money ${moneyToneClass(out)}`.trim()}>{out}</span>;
    },
  };
};

// Page + page size survive opening a record and coming back (2026-10-09: "on page 5, view
// one LPO, back takes me all the way to page 1 - same on stock in / stock out"). The list
// unmounts when its detail route opens, so the position is kept in sessionStorage, keyed by
// the route and the table's columns (two tables on one page keep separate positions).
const readPaging = (key) => {
  try {
    const v = JSON.parse(sessionStorage.getItem(key) || "null");
    return v && v.current > 0 && v.pageSize > 0 ? v : null;
  } catch {
    return null;
  }
};

const DataTable = ({ columns, dataSource, rowKey = "id", loading = false, summary, onRow, expandable, rowSelection }) => {
  const tinted = useMemo(() => (columns || []).map(tintColumn), [columns]);
  const { pathname } = useLocation();
  const storeKey = `tbl:${pathname}:${(columns || []).map((c) => c.key || c.dataIndex || c.title).join(",")}`;
  const [paging, setPaging] = useState(() => readPaging(storeKey) || { current: 1, pageSize: 10 });
  const total = Array.isArray(dataSource) ? dataSource.length : 0;
  // A filter can shrink the list below the saved page - fall back to the last real page.
  const lastPage = Math.max(1, Math.ceil(total / paging.pageSize));
  const current = !loading && total > 0 ? Math.min(paging.current, lastPage) : paging.current;
  const onPageChange = (page, size) => {
    const next = { current: size !== paging.pageSize ? 1 : page, pageSize: size };
    setPaging(next);
    try {
      sessionStorage.setItem(storeKey, JSON.stringify(next));
    } catch {
      // storage blocked - the position simply is not remembered
    }
  };
  return (
  <Table
    className="table datanew dataTable no-footer tx-coloured"
    columns={tinted}
    dataSource={dataSource}
    rowKey={rowKey}
    loading={loading}
    // Optional checkbox column (e.g. Leave Management's bulk-mark grid) -
    // antd's own `rowSelection` prop ({selectedRowKeys, onChange, ...}),
    // forwarded straight through. Undefined for every existing caller, so
    // no visual change unless a page opts in.
    rowSelection={rowSelection}
    // Optional grand-totals row - antd's own `summary` render-prop, forwarded
    // straight through. Undefined for every existing caller, so no visual
    // change unless a page opts in.
    summary={summary}
    // Optional whole-row click handler (e.g. a drill-down report where
    // every cell, not just one link column, should navigate) - antd's own
    // `onRow` render-prop, forwarded straight through. Undefined for every
    // existing caller, so no behavior change unless a page opts in.
    onRow={onRow}
    // Optional expand-in-place row detail (e.g. a report row with no real
    // detail page of its own) - antd's own `expandable` prop
    // ({ expandedRowRender, rowExpandable }), forwarded straight through.
    // Undefined for every existing caller, so no behavior change unless a
    // page opts in.
    expandable={expandable}
    // Controlled (2026-10-09): current page and size come from the saved state above, so
    // the size changer works and the position survives a round trip to a detail page.
    pagination={{ current, pageSize: paging.pageSize, onChange: onPageChange, showSizeChanger: true, pageSizeOptions: [10, 20, 50, 100] }}
  />
  );
};

export default DataTable;
